import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PackageStatusBadge, PACKAGE_STATUS_LABELS } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Printer, Store, User, Bike, MapPin, CheckCircle2 } from "lucide-react";
import { escapeHtml, openPrintWindow } from "@/lib/printHelper";

const TIMELINE_STEPS = [
  { key: "CREATED", label: "Package Created by Store Owner" },
  { key: "WAITING_FOR_PICKUP", label: "Waiting for Pickup" },
  { key: "PICKED_UP_BY_RIDER", label: "Picked Up by Rider" },
  { key: "RECEIVED_AT_WAREHOUSE", label: "Received at Warehouse" },
  { key: "SHIPPED_OUT", label: "Shipped Out from Warehouse" },
  { key: "OUT_FOR_LAST_MILE", label: "Picked Up for Last-Mile Delivery" },
  { key: "DELIVERED", label: "Delivered to Customer" },
];

const ORDER = [
  "WAITING_FOR_PICKUP", "PICKED_UP_BY_RIDER", "RECEIVED_AT_WAREHOUSE",
  "SHIPPED_OUT", "OUT_FOR_LAST_MILE", "DELIVERED",
];

export default function PackageDetailDialog({ packageId, onOpenChange, onUpdated }) {
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!packageId) { setPkg(null); return; }
    setLoading(true);
    api
      .get(`/admin/packages/${packageId}`)
      .then((r) => { setPkg(r.data); setNewStatus(r.data.status); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [packageId]);

  const reachedStatuses = new Set();
  if (pkg) {
    reachedStatuses.add("CREATED");
    (pkg.history || []).forEach((h) => reachedStatuses.add(h.status));
  }

  const submitStatus = async () => {
    if (!pkg || newStatus === pkg.status) return;
    setSaving(true);
    try {
      await api.post(`/admin/packages/${pkg.package_id}/status`, { status: newStatus, remarks: "" });
      toast.success(`Status updated to ${PACKAGE_STATUS_LABELS[newStatus]}.`);
      const r = await api.get(`/admin/packages/${pkg.package_id}`);
      setPkg(r.data);
      onUpdated?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const printLabel = () => {
    if (!pkg) return;
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(pkg.package_id)} – Label</title>
      <style>
        body { font-family: ui-monospace, Menlo, monospace; padding: 24px; }
        .box { border: 2px solid #000; padding: 16px; }
        h1 { font-family: system-ui; margin: 0 0 8px; }
        .row { margin: 4px 0; }
        img { width: 240px; height: 240px; image-rendering: pixelated; }
      </style></head><body>
      <div class="box">
        <h1>DeliveryBoss</h1>
        <div class="row"><b>Package:</b> ${escapeHtml(pkg.package_id)}</div>
        <div class="row"><b>To:</b> ${escapeHtml(pkg.receiver_name)}</div>
        <div class="row">${escapeHtml(pkg.receiver_address)}</div>
        <div class="row"><b>Phone:</b> ${escapeHtml(pkg.receiver_phone)}</div>
        <div class="row"><b>From:</b> ${escapeHtml(pkg.store_name)}</div>
        <div style="margin-top:16px;text-align:center;">
          <img src="${escapeHtml(pkg.qr_image)}" alt="QR" />
        </div>
      </div>
      <script>window.onload=()=>window.print();</script>
      </body></html>`;
    openPrintWindow(html, "width=480,height=640");
  };

  return (
    <Dialog open={!!packageId} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white max-w-4xl max-h-[90vh] overflow-y-auto scrollbar-thin"
        data-testid="package-detail-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-3">
            <span>Package Details</span>
            {pkg && <PackageStatusBadge status={pkg.status} />}
          </DialogTitle>
          <DialogDescription className="text-slate-600 dark:text-slate-400 font-mono-tech text-xs">
            {pkg ? pkg.package_id : "Loading…"}
          </DialogDescription>
        </DialogHeader>

        {loading || !pkg ? (
          <div className="py-10 text-center text-slate-500">Loading package…</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mt-2">
            {/* LEFT: info */}
            <div className="lg:col-span-3 space-y-5">
              <section className="border border-slate-200 dark:border-slate-800 rounded-lg p-4 bg-slate-100/40 dark:bg-slate-900/40">
                <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-500 mb-3">
                  / Package
                </div>
                <div className="text-lg font-display font-semibold mb-1">{pkg.package_name}</div>
                <div className="text-sm text-slate-600 dark:text-slate-400">{pkg.description}</div>
              </section>

              <section className="border border-slate-200 dark:border-slate-800 rounded-lg p-4 bg-slate-100/40 dark:bg-slate-900/40">
                <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-500 mb-3">
                  / Items
                </div>
                <ul className="space-y-2 text-sm">
                  {(pkg.items || []).map((it, i) => (
                    <li
                      key={`${it.item_name}-${i}`}
                      data-testid={`pkg-item-${i}`}
                      className="flex justify-between border-b border-slate-200/60 dark:border-slate-800/60 pb-2 last:border-0"
                    >
                      <span className="text-slate-200">{it.item_name}</span>
                      <span className="text-cyan-300 font-mono-tech text-xs">×{it.quantity}</span>
                    </li>
                  ))}
                </ul>
              </section>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <section className="border border-slate-200 dark:border-slate-800 rounded-lg p-4 bg-slate-100/40 dark:bg-slate-900/40">
                  <div className="flex items-center gap-2 mb-2 text-cyan-400">
                    <Store className="w-4 h-4" />
                    <div className="text-[10px] font-mono-tech uppercase tracking-[0.2em] text-slate-500">Store</div>
                  </div>
                  <div className="text-sm text-slate-900 dark:text-white font-medium">{pkg.store_name}</div>
                  <div className="text-xs text-slate-600 dark:text-slate-400">{pkg.store_owner_name}</div>
                </section>
                <section className="border border-slate-200 dark:border-slate-800 rounded-lg p-4 bg-slate-100/40 dark:bg-slate-900/40">
                  <div className="flex items-center gap-2 mb-2 text-cyan-400">
                    <User className="w-4 h-4" />
                    <div className="text-[10px] font-mono-tech uppercase tracking-[0.2em] text-slate-500">Receiver</div>
                  </div>
                  <div className="text-sm text-slate-900 dark:text-white font-medium">{pkg.receiver_name}</div>
                  <div className="text-xs text-slate-600 dark:text-slate-400">{pkg.receiver_phone}</div>
                  <div className="text-xs text-slate-500 mt-1 flex items-start gap-1">
                    <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                    <span>{pkg.receiver_address}</span>
                  </div>
                </section>
                <section className="border border-slate-200 dark:border-slate-800 rounded-lg p-4 bg-slate-100/40 dark:bg-slate-900/40">
                  <div className="flex items-center gap-2 mb-2 text-cyan-400">
                    <Bike className="w-4 h-4" />
                    <div className="text-[10px] font-mono-tech uppercase tracking-[0.2em] text-slate-500">Rider</div>
                  </div>
                  <div className="text-sm text-slate-900 dark:text-white font-medium">
                    {pkg.assigned_rider_name || <span className="text-slate-500">Unassigned</span>}
                  </div>
                </section>
              </div>

              <section className="border border-slate-200 dark:border-slate-800 rounded-lg p-4 bg-slate-100/40 dark:bg-slate-900/40">
                <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-500 mb-4">
                  / Status Timeline
                </div>
                <ol className="space-y-4">
                  {TIMELINE_STEPS.map((step, idx) => {
                    const reached = reachedStatuses.has(step.key) ||
                      (step.key === "CREATED");
                    const isCurrent = pkg.status === step.key ||
                      (step.key === "CREATED" && pkg.status === "WAITING_FOR_PICKUP" && idx === 0 && false);
                    const histEntry = (pkg.history || []).find((h) => h.status === step.key);
                    return (
                      <li
                        key={step.key}
                        data-testid={`timeline-step-${step.key}`}
                        className="flex items-start gap-3"
                      >
                        <div className={`mt-0.5 w-7 h-7 rounded-full grid place-items-center border ${
                          reached
                            ? "bg-cyan-500/10 border-cyan-500/50 text-cyan-400"
                            : "bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-600"
                        }`}>
                          {reached ? <CheckCircle2 className="w-4 h-4" /> : idx + 1}
                        </div>
                        <div className="flex-1">
                          <div className={`text-sm font-medium ${reached ? "text-slate-900 dark:text-white" : "text-slate-500"}`}>
                            {step.label}
                          </div>
                          {histEntry && (
                            <div className="text-[11px] text-slate-500 font-mono-tech mt-0.5">
                              {new Date(histEntry.created_at).toLocaleString()}
                              {histEntry.updated_by ? ` · ${histEntry.updated_by}` : ""}
                            </div>
                          )}
                          {isCurrent && (
                            <div className="text-[10px] inline-block mt-1 px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-300 border border-cyan-500/30">
                              CURRENT
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </section>
            </div>

            {/* RIGHT: QR + actions */}
            <div className="lg:col-span-2 space-y-5">
              <section className="border border-slate-200 dark:border-slate-800 rounded-lg p-4 bg-slate-100/40 dark:bg-slate-900/40 text-center">
                <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-500 mb-3">
                  / QR Code
                </div>
                <div className="bg-white p-4 rounded-md inline-block">
                  {pkg.qr_image && (
                    <img
                      src={pkg.qr_image}
                      alt={`QR for ${pkg.package_id}`}
                      className="w-44 h-44"
                      data-testid="package-qr-image"
                    />
                  )}
                </div>
                <div className="mt-3 font-mono-tech text-xs text-slate-600 dark:text-slate-400">
                  {pkg.qr_code_data}
                </div>
                <Button
                  data-testid="print-label-btn"
                  variant="outline"
                  onClick={printLabel}
                  className="mt-3 w-full bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-200 hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white"
                >
                  <Printer className="w-4 h-4 mr-2" /> Print Label
                </Button>
              </section>

              <section className="border border-slate-200 dark:border-slate-800 rounded-lg p-4 bg-slate-100/40 dark:bg-slate-900/40">
                <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-500 mb-3">
                  / Update Status
                </div>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger
                    data-testid="status-select-trigger"
                    className="w-full bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white">
                    {ORDER.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`status-option-${s}`}>
                        {PACKAGE_STATUS_LABELS[s]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  data-testid="update-status-btn"
                  onClick={submitStatus}
                  disabled={saving || newStatus === pkg.status}
                  className="mt-3 w-full bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-medium"
                >
                  {saving ? "Updating…" : "Update Status"}
                </Button>
              </section>

              <section className="border border-slate-200 dark:border-slate-800 rounded-lg p-4 bg-slate-100/40 dark:bg-slate-900/40 text-xs text-slate-600 dark:text-slate-400 space-y-1.5">
                <div className="flex justify-between">
                  <span className="text-slate-500">Created</span>
                  <span>{new Date(pkg.created_at).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Last Updated</span>
                  <span>{new Date(pkg.updated_at).toLocaleString()}</span>
                </div>
                {pkg.delivered_at && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Delivered</span>
                    <span className="text-green-300">{new Date(pkg.delivered_at).toLocaleString()}</span>
                  </div>
                )}
              </section>

              {pkg.delivery_proof && (
                <section
                  data-testid="delivery-proof-section"
                  className="border border-green-500/30 rounded-lg p-4 bg-green-500/5"
                >
                  <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-green-300 mb-3">
                    / Delivery Proof
                  </div>
                  <div className="space-y-3">
                    <div>
                      <div className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-500 mb-1">
                        Package Photo
                      </div>
                      <img
                        src={pkg.delivery_proof.proof_photo}
                        alt="Delivery proof"
                        data-testid="delivery-proof-photo"
                        className="w-full rounded-md border border-slate-300 dark:border-slate-700"
                      />
                    </div>
                    <div>
                      <div className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-500 mb-1">
                        Receiver Signature
                      </div>
                      <div className="bg-white rounded-md p-2">
                        <img
                          src={pkg.delivery_proof.signature_image}
                          alt="Receiver signature"
                          data-testid="delivery-proof-signature"
                          className="w-full max-h-32 object-contain"
                        />
                      </div>
                    </div>
                    <div className="text-xs space-y-0.5">
                      <div className="text-slate-600 dark:text-slate-400">
                        Signed by:{" "}
                        <span className="text-slate-900 dark:text-white">
                          {pkg.delivery_proof.receiver_signed_name || "—"}
                        </span>
                      </div>
                      <div className="text-slate-500 font-mono-tech text-[10px]">
                        by {pkg.delivery_proof.captured_by_rider_name} ·{" "}
                        {new Date(pkg.delivery_proof.captured_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </section>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
